import React, {useCallback, useMemo, useState} from "react";
import {CSSTransition, SwitchTransition} from "react-transition-group";
import {Asset, ItineraryDay, TourOperator, TripItinerary} from "./contentfulModel.ts";
import {ItineraryMap} from "./ItineraryMap.tsx";
import SVGComponent from "./SVGComponent.tsx";
import {MiceLightbox} from "./miceLightbox.tsx";
import {MiceStreetMap} from "./miceStreetMap.tsx";
import {cdnImageWidth, LOCATIONIQ_KEY, parseMiceSubtitle, textColorOn} from "./utils.tsx";

// Campos reales del JSON que contentfulModel.ts no recoge
interface MiceOtherTab {
    title?: string;
    content?: { title?: string; description?: string; image?: Asset }[];
    mainImage?: Asset;
}

export type MicePackage = TripItinerary & {
    countryCodes?: string;
    mapImageDigital?: Asset;
    otherTabs?: MiceOtherTab[];
    itinerary: (ItineraryDay & { locations?: { name?: string; coordinates?: { latitude?: string; longitude?: string } }[] })[];
};

const INK = '#1D1B18';
const FOCUS_RING = 'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]';

const SINGULAR_TYPE: Record<string, string> = {
    hoteles: 'Hotel',
    campamentos: 'Campamento',
    restaurantes: 'Restaurante',
    venues: 'Venue',
};

const findOtherTab = (pkg: MicePackage, title: string) =>
    pkg.otherTabs?.find(t => t.title?.trim().toLowerCase() === title);

// Cifras clave a partir de otherTabs y subtitle; solo se devuelven las que existen en los datos
const getKeyFigures = (pkg: MicePackage) => {
    const rooms = findOtherTab(pkg, 'habitaciones')?.content
        ?.find(c => c.title?.toLowerCase() === 'total')?.description?.match(/\d[\d.]*/)?.[0];
    // Hoteles y campamentos usan "Salas y espacios"; restaurantes, "Salas y capacidades"
    const summary = (findOtherTab(pkg, 'salas y espacios') ?? findOtherTab(pkg, 'salas y capacidades'))?.content
        ?.find(c => c.title?.toLowerCase() === 'resumen')?.description ?? '';
    const halls = summary.match(/(\d+)\s*salas?/i)?.[1];
    const capacity = summary.match(/capacidad total:\s*([\d.]+)/i)?.[1]
        ?? `${summary} ${pkg.subtitle ?? ''}`.match(/hasta\s*([\d.]+)\s*(?:personas|pax|comensales)/i)?.[1];
    const tents = pkg.subtitle?.match(/(\d+)\s*tiendas/i)?.[1];
    const restaurants = findOtherTab(pkg, 'restauración')?.content?.length;

    return [
        {label: 'Habitaciones', value: rooms},
        {label: 'Tiendas', value: tents},
        {label: 'Salas', value: halls},
        {label: 'Capacidad', value: capacity},
        {label: 'Restaurantes', value: restaurants ? String(restaurants) : undefined},
    ].filter((f): f is { label: string; value: string } => !!f.value).slice(0, 3);
};

const BackIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 12H5"/>
        <path d="M12 19l-7-7 7-7"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-px">
        <path d="M20 6L9 17l-5-5"/>
    </svg>
);

interface MiceCatalogContentProps {
    tourOperator: TourOperator;
    tourPackage: MicePackage;
    goToSlide: (slideIndex: number) => void;
    // Al volver, el índice se abre en la ciudad y el tipo de esta ficha
    onBackToIndex?: (selection: { city?: string; type?: string }) => void;
}

type MiceTab = { id: string; label: string };

export const MiceCatalogContent: React.FC<MiceCatalogContentProps> = ({tourOperator, tourPackage, goToSlide, onBackToIndex}) => {
    const accent = tourOperator.primaryColor || '#E9CCCC';
    const onAccent = textColorOn(accent);
    const isMobile = useMemo(() => window.innerWidth < 768, []);
    const [activeTab, setActiveTab] = useState('introduction');
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const nodeRef = React.useRef(null);

    const tabs = useMemo<MiceTab[]>(() => [
        {id: 'introduction', label: 'Introducción'},
        {id: 'situation', label: 'Situación'},
        ...(tourPackage.termsAndPricing?.includes?.length ? [{id: 'services', label: 'Servicios'}] : []),
        ...(tourPackage.otherTabs ?? []).map(t => ({id: `other-${t.title}`, label: t.title ?? ''})),
        ...(tourPackage.tripBookUrl ? [{id: 'video', label: 'Vídeo'}] : []),
    ], [tourPackage]);

    const handleBackClick = useCallback(() => {
        onBackToIndex?.({city: tourPackage.category, type: tourPackage.subcategory});
        goToSlide(1);
        setActiveTab('introduction');
    }, [goToSlide, onBackToIndex, tourPackage]);

    const {stars, rest} = parseMiceSubtitle(tourPackage.subtitle);
    const typeLabel = SINGULAR_TYPE[tourPackage.subcategory?.trim().toLowerCase() ?? ''] ?? tourPackage.subcategory;
    const location = tourPackage.itinerary?.[0];
    const gallery = useMemo(() => {
        const files = [tourPackage.mainImage?.file, ...(location?.images ?? []).map(i => i.file)]
            .filter((f): f is string => !!f);
        return Array.from(new Set(files)).slice(0, 3);
    }, [tourPackage, location]);

    // Todas las fotos de la ficha para la galería ampliada, sin repetir
    const allImages = useMemo(() => {
        const files = [
            tourPackage.mainImage?.file,
            ...(location?.images ?? []).map(i => i.file),
            tourPackage.introductionImage?.file,
            tourPackage.termsAndPricing?.includesImage?.file,
            tourPackage.termsAndPricing?.observationImage?.file,
            ...(tourPackage.otherTabs ?? []).flatMap(t => [t.mainImage?.file, ...(t.content ?? []).map(c => c.image?.file)]),
        ].filter((f): f is string => !!f);
        return Array.from(new Set(files));
    }, [tourPackage, location]);

    const openLightbox = (file?: string) => setLightboxIndex(Math.max(0, file ? allImages.indexOf(file) : 0));
    const closeLightbox = useCallback(() => setLightboxIndex(null), []);
    const photoButton = `block p-0 border-0 bg-transparent cursor-zoom-in overflow-hidden ${FOCUS_RING}`;

    const chips = (
        <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 rounded-full text-xs md:text-sm font-semibold" style={{backgroundColor: accent, color: onAccent}}>
                {[typeLabel, stars].filter(Boolean).join(' ')}
            </span>
            <span className="px-3 py-1.5 rounded-full text-xs md:text-sm bg-white text-[#4A443C]">
                {[location?.title, tourPackage.category].filter(Boolean).join(', ')}
            </span>
        </div>
    );

    const renderIntroduction = () => {
        const figures = getKeyFigures(tourPackage);
        const observations: { text?: string }[] = tourPackage.termsAndPricing?.observations ?? [];
        return (
            <div className="flex flex-col md:flex-row gap-6 md:gap-12 md:h-full">
                {isMobile && gallery[0] && (
                    <button type="button" onClick={() => openLightbox(gallery[0])} aria-label={`Ver las ${allImages.length} fotos`}
                            className={`relative w-full h-[260px] rounded-b-3xl ${photoButton}`}>
                        <img src={cdnImageWidth(gallery[0], 800)} alt={tourPackage.name} className="w-full h-full object-cover"/>
                        {allImages.length > 1 && (
                            <span className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-white/90 text-xs font-semibold" style={{color: INK}}>
                                {allImages.length} fotos
                            </span>
                        )}
                    </button>
                )}
                <section className="md:w-[560px] md:flex-shrink-0 flex flex-col justify-center gap-4 md:gap-6 px-5 md:px-0">
                    {chips}
                    <h1 className="m-0 text-2xl md:text-[44px] leading-tight font-bold" style={{fontFamily: 'var(--heading-font)'}}>
                        {tourPackage.name}
                    </h1>
                    {rest.length > 0 && <p className="m-0 text-sm text-[#6B645B]">{rest.join(' · ')}</p>}
                    {figures.length > 0 && (
                        <dl className="m-0 grid gap-2 md:gap-3" style={{gridTemplateColumns: `repeat(${figures.length}, minmax(0, 1fr))`}}>
                            {figures.map(f => (
                                <div key={f.label} className="p-3 md:p-[18px] bg-white rounded-xl md:rounded-2xl">
                                    <dt className="text-[11px] md:text-xs text-[#6B645B] uppercase tracking-wider">{f.label}</dt>
                                    <dd className="m-0 mt-1 text-xl md:text-3xl font-bold">{f.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                    <p className="m-0 text-sm md:text-base leading-relaxed text-[#4A443C]">{tourPackage.description}</p>
                    {observations.map((o, i) => o.text && (
                        <p key={i} className="m-0 text-xs text-[#6B645B]">{o.text}</p>
                    ))}
                </section>
                {!isMobile && gallery.length > 0 && (
                    <section aria-label="Galería" className="flex-1 min-w-0 grid grid-cols-2 grid-rows-2 gap-3">
                        {gallery.map((file, i) => {
                            const isLast = i === gallery.length - 1;
                            const moreCount = allImages.length - gallery.length;
                            return (
                                <button key={file} type="button" onClick={() => openLightbox(file)}
                                        aria-label={`Ampliar foto ${i + 1} de ${allImages.length}`}
                                        className={`relative w-full h-full rounded-3xl group ${photoButton} ${i === 0 ? 'col-span-2' : ''} ${gallery.length === 1 ? 'row-span-2' : ''}`}>
                                    <img src={cdnImageWidth(file, i === 0 ? 1400 : 800)} alt={`${tourPackage.name} ${i + 1}`}
                                         className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"/>
                                    {isLast && moreCount > 0 && (
                                        <span className="absolute bottom-4 right-4 px-4 py-2 rounded-full bg-white/90 text-sm font-semibold" style={{color: INK}}>
                                            Ver las {allImages.length} fotos
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </section>
                )}
            </div>
        );
    };

    const renderSituation = () => {
        // Callejero si hay clave de LocationIQ y coordenadas; si no, el mapa de país de siempre
        const coords = location?.locations?.[0]?.coordinates;
        const lat = Number(coords?.latitude);
        const lng = Number(coords?.longitude);
        const hasStreetMap = !!LOCATIONIQ_KEY && Number.isFinite(lat) && Number.isFinite(lng) && !!coords?.latitude;
        const hasMap = hasStreetMap || !!tourPackage.countryCodes || !!tourPackage.mapImageDigital?.file;
        return (
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 md:h-full px-5 md:px-0 pt-20 md:pt-0">
                {hasMap && (
                    <section aria-label="Mapa" className="w-full md:w-[46%] md:flex-shrink-0 h-[300px] md:h-full rounded-3xl overflow-hidden bg-[#EDE7DC]">
                        {hasStreetMap ? (
                            <MiceStreetMap latitude={lat} longitude={lng} name={tourPackage.name} accent={accent}/>
                        ) : tourPackage.mapImageDigital?.file ? (
                            <SVGComponent svgUrl={tourPackage.mapImageDigital.file} className="w-full h-full object-cover"/>
                        ) : (
                            <ItineraryMap version="web"
                                          tourOperator={{...tourOperator, secondaryColor: (tourOperator as { secondaryColor?: string }).secondaryColor || '#D9CFC0'}}
                                          itineraryDays={tourPackage.itinerary}
                                          countryCodes={tourPackage.countryCodes!.split(',')}/>
                        )}
                    </section>
                )}
                <section className="flex-1 min-w-0 flex flex-col justify-center gap-6 md:gap-7">
                    <div className="flex flex-col gap-2">
                        <div className="text-xs font-semibold tracking-[0.14em] uppercase text-[#6B645B]">Ubicación</div>
                        <h1 className="m-0 text-2xl md:text-[40px] leading-tight font-bold" style={{fontFamily: 'var(--heading-font)'}}>
                            {[location?.title, tourPackage.category].filter(Boolean).join(', ')}
                        </h1>
                        <p className="m-0 text-sm md:text-[15px] leading-relaxed text-[#4A443C]">{location?.description}</p>
                    </div>
                    {location?.images?.length ? (
                        <div className="grid grid-cols-3 gap-3">
                            {location.images.slice(0, 3).map((img, i) => (
                                <button key={i} type="button" onClick={() => openLightbox(img.file)}
                                        aria-label={`Ampliar foto ${i + 1}`}
                                        className={`w-full h-[100px] md:h-[150px] rounded-xl md:rounded-2xl ${photoButton}`}>
                                    <img src={cdnImageWidth(img.file, 480)} alt={`${tourPackage.name} ${i + 1}`}
                                         className="w-full h-full object-cover"/>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </section>
            </div>
        );
    };

    const renderServices = () => (
        <div className="flex flex-col md:flex-row gap-6 md:h-full px-5 md:px-0 pt-20 md:pt-0">
            <section className="flex-1 min-w-0 bg-white rounded-3xl p-6 md:p-8 flex flex-col gap-5 md:overflow-y-auto">
                <h2 className="m-0 text-xl md:text-[22px] font-bold">Servicios</h2>
                <ul className="list-none m-0 p-0 grid md:grid-cols-2 gap-x-8 gap-y-3.5">
                    {tourPackage.termsAndPricing.includes.map((item, i) => (
                        <li key={i} className="flex gap-3 items-start text-sm leading-normal text-[#4A443C]">
                            <CheckIcon/>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </section>
            {!isMobile && tourPackage.termsAndPricing.includesImage?.file && (
                <img src={cdnImageWidth(tourPackage.termsAndPricing.includesImage.file, 900)} alt={tourPackage.name}
                     className="w-[36%] flex-shrink-0 h-full object-cover rounded-3xl"/>
            )}
        </div>
    );

    // Habitaciones, Salas y espacios, Restauración… vienen como otherTabs con pares título/descripción
    const renderOtherTab = (otherTab: MiceOtherTab) => {
        const image = otherTab.mainImage?.file ?? gallery[1] ?? gallery[0];
        return (
            <div className="flex flex-col md:flex-row gap-6 md:h-full px-5 md:px-0 pt-20 md:pt-0">
                <section className="flex-1 min-w-0 bg-white rounded-3xl p-6 md:p-8 flex flex-col gap-5 md:overflow-y-auto">
                    <h2 className="m-0 text-xl md:text-[22px] font-bold">{otherTab.title}</h2>
                    <dl className="m-0 flex flex-col">
                        {(otherTab.content ?? []).map((row, i) => (
                            <div key={i} className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1 md:gap-6 py-3.5 border-b border-[#EFEBE4] last:border-b-0">
                                <dt className="text-sm md:text-[15px] font-semibold">{row.title}</dt>
                                {row.description && (
                                    <dd className="m-0 text-sm text-[#4A443C] md:text-right md:max-w-[60%]">{row.description}</dd>
                                )}
                            </div>
                        ))}
                    </dl>
                </section>
                {!isMobile && image && (
                    <img src={cdnImageWidth(image, 900)} alt={otherTab.title} className="w-[36%] flex-shrink-0 h-full object-cover rounded-3xl"/>
                )}
            </div>
        );
    };

    const renderVideo = () => (
        <div className="w-full h-[70vh] md:h-full pt-16 md:pt-0">
            <iframe src={tourPackage.tripBookUrl} title={`Vídeo · ${tourPackage.name}`}
                    className="w-full h-full border-0 rounded-3xl bg-black" allowFullScreen/>
        </div>
    );

    const renderTab = () => {
        switch (activeTab) {
            case 'introduction':
                return renderIntroduction();
            case 'situation':
                return renderSituation();
            case 'services':
                return renderServices();
            case 'video':
                return renderVideo();
            default: {
                const otherTab = tourPackage.otherTabs?.find(t => `other-${t.title}` === activeTab);
                return otherTab ? renderOtherTab(otherTab) : null;
            }
        }
    };

    const tabButtons = tabs.map(tab => {
        const active = tab.id === activeTab;
        return (
            <button key={tab.id} type="button" aria-current={active ? 'page' : undefined}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 flex items-center min-h-[44px] px-3.5 md:px-4 rounded-[10px] text-[13px] md:text-sm cursor-pointer border-0 ${active ? 'font-semibold' : `text-[#4A443C] ${isMobile ? 'bg-white' : 'bg-transparent hover:bg-[#F7F6F2]'}`} focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]`}
                    style={active ? {backgroundColor: accent, color: onAccent} : undefined}>
                {tab.label}
            </button>
        );
    });

    return (
        <div className="relative flex flex-col w-full h-full overflow-hidden bg-[#F7F6F2] text-left" style={{color: INK}}>
            {isMobile ? (
                <button type="button" onClick={handleBackClick} aria-label="Volver al índice"
                        className="absolute top-4 left-4 z-20 size-11 p-0 rounded-xl bg-white/90 flex items-center justify-center shadow-md border-0 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]">
                    <BackIcon/>
                </button>
            ) : (
                <header className="h-[88px] flex-shrink-0 flex items-center justify-between gap-6 px-14">
                    <button type="button" onClick={handleBackClick}
                            className="flex items-center gap-2.5 min-h-[44px] px-[18px] rounded-xl bg-white text-sm font-semibold cursor-pointer border-0 flex-shrink-0 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]">
                        <BackIcon/>
                        {[tourPackage.category, tourPackage.subcategory].filter(Boolean).join(' · ')}
                    </button>
                    <nav aria-label="Secciones de la ficha" className="swiper-no-swiping flex gap-1 p-1 bg-white rounded-[14px] overflow-x-auto hide-scrollbar min-w-0">
                        {tabButtons}
                    </nav>
                </header>
            )}

            <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${isMobile ? 'pb-28' : 'px-14 pb-14 pt-2'}`}>
                <SwitchTransition mode="out-in">
                    <CSSTransition key={activeTab} timeout={300} classNames="fade" nodeRef={nodeRef}>
                        {/* Solo en escritorio ocupa el alto exacto; en móvil crece con el contenido para que haya scroll */}
                        <div ref={nodeRef} className="w-full md:h-full">
                            {renderTab()}
                        </div>
                    </CSSTransition>
                </SwitchTransition>
            </div>

            {isMobile && (
                <nav aria-label="Secciones de la ficha"
                     className="swiper-no-swiping absolute left-0 right-0 bottom-0 z-20 flex gap-1.5 px-3 pt-3 pb-5 overflow-x-auto hide-scrollbar bg-[#F7F6F2]/95 backdrop-blur">
                    {tabButtons}
                </nav>
            )}

            {lightboxIndex !== null && allImages.length > 0 && (
                <MiceLightbox images={allImages} startIndex={lightboxIndex} title={tourPackage.name} onClose={closeLightbox}/>
            )}
        </div>
    );
};
